/**
 * Letterboxd CSV import (PLAN §5). Entirely client-side: the dropped file is
 * unzipped and parsed in memory and never leaves the browser — said plainly
 * below, not just in this comment.
 *
 * Flow: drop → unzip/parse/match → confirm uncertain matches in MatchReview →
 * review the diff (additive by default, "replace" is opt-in) → commit.
 */

import type { EntryId } from "@schema/catalog.ts";
import type { WatchRecord } from "@schema/persist.ts";
import { type DragEvent, useEffect, useMemo, useRef, useState } from "react";
import { useCatalog } from "@/lib/catalog/context.tsx";
import { computeImportDiff, type ImportDiff, type ImportMode } from "@/lib/letterboxd/diff.ts";
import { findMatch, type MatchOutcome } from "@/lib/letterboxd/match.ts";
import { type LetterboxdFilmRecord, mergeLetterboxdExport } from "@/lib/letterboxd/rows.ts";
import { tvDegradation } from "@/lib/letterboxd/tv.ts";
import { unpackLetterboxdExport } from "@/lib/letterboxd/unzip.ts";
import { useProgressStore } from "@/state/progress.ts";
import styles from "./ImportDialog.module.css";
import { MatchReview, type ReviewRow } from "./MatchReview.tsx";

interface RowMatch {
  record: LetterboxdFilmRecord;
  outcome: MatchOutcome;
}

type Phase =
  | { kind: "drop" }
  | { kind: "error"; message: string }
  | { kind: "parsed"; rows: RowMatch[] }
  | { kind: "done"; diff: ImportDiff };

function toRecord(row: LetterboxdFilmRecord): WatchRecord {
  return {
    watchedAt: row.watchedDate,
    rating10: row.rating10,
    rewatch: row.rewatch,
    source: "letterboxd",
  };
}

export function ImportDialog() {
  const catalog = useCatalog();
  const entries = catalog.entries;
  const entryById = useMemo(() => new Map(entries.map((e) => [e.id, e])), [entries]);
  const titleById = useMemo(
    () => Object.fromEntries(entries.map((e) => [e.id, e.title])),
    [entries],
  );

  const currentRecords = useProgressStore((s) => s.records);
  const watched = useProgressStore((s) => s.watched);
  const applyImport = useProgressStore((s) => s.applyImport);
  const markAllWatched = useProgressStore((s) => s.markAllWatched);

  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<Phase>({ kind: "drop" });
  const [mode, setMode] = useState<ImportMode>("additive");
  const [resolutions, setResolutions] = useState<Record<number, EntryId | null>>({});
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  const tv = useMemo(() => tvDegradation(entries), [entries]);
  const tvAiredUnwatched = tv.airedEntryIds.filter((id) => !watched[id]);

  const reset = () => {
    setPhase({ kind: "drop" });
    setResolutions({});
    setMode("additive");
  };

  const close = () => setOpen(false);

  async function handleFile(file: File) {
    try {
      const files = await unpackLetterboxdExport(file);
      if (!files.diary && !files.watched && !files.ratings) {
        setPhase({
          kind: "error",
          message:
            "Couldn't find watched.csv, diary.csv, or ratings.csv in that file. Drop the .zip from Letterboxd's Settings → Import & Export, or one of those .csv files directly.",
        });
        return;
      }
      const merged = mergeLetterboxdExport(files);
      const rows = merged.map((record) => ({ record, outcome: findMatch(record, entries) }));
      setResolutions({});
      setPhase({ kind: "parsed", rows });
    } catch {
      setPhase({ kind: "error", message: "Couldn't read that file. Is it a Letterboxd export?" });
    }
  }

  const rowMatches = phase.kind === "parsed" ? phase.rows : [];
  const reviewRows: ReviewRow[] = rowMatches
    .map((rm, index) => ({ index, record: rm.record, outcome: rm.outcome }))
    .filter((r) => r.outcome.status === "review");
  const unmatchedRows = rowMatches
    .map((rm, index) => ({ index, record: rm.record, outcome: rm.outcome }))
    .filter((r) => r.outcome.status === "unmatched");
  const autoMatchedCount = rowMatches.filter((rm) => rm.outcome.status === "matched").length;

  const importedRecords = useMemo(() => {
    const out: Record<EntryId, WatchRecord> = {};
    rowMatches.forEach((rm, index) => {
      let entryId: EntryId | null = null;
      if (rm.outcome.status === "matched") entryId = rm.outcome.hit?.entryId ?? null;
      else if (rm.outcome.status === "review") entryId = resolutions[index] ?? null;
      if (entryId) out[entryId] = toRecord(rm.record);
    });
    return out;
  }, [rowMatches, resolutions]);

  const diff = useMemo(
    () => computeImportDiff(currentRecords, importedRecords, mode, titleById),
    [currentRecords, importedRecords, mode, titleById],
  );

  const importedCount = Object.keys(importedRecords).length;

  return (
    <>
      <button type="button" className={styles.trigger} onClick={() => setOpen(true)}>
        Import from Letterboxd
      </button>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: click here is backdrop-dismissal only (native ::backdrop clicks land on the <dialog> itself); a modal <dialog> already closes on Escape natively. */}
      <dialog
        ref={dialogRef}
        className={styles.dialog}
        aria-label="Import from Letterboxd"
        onClose={() => {
          setOpen(false);
          reset();
        }}
        onClick={(event) => {
          if (event.target === dialogRef.current) close();
        }}
      >
        <div className={styles.inner}>
          <div className={styles.head}>
            <h2 className={styles.title}>Import from Letterboxd</h2>
            <button type="button" className={styles.closeButton} onClick={close}>
              Close
            </button>
          </div>

          <p className={styles.privacyNote}>
            Entirely client-side: your export is unzipped and matched right here in the browser.
            Nothing is uploaded anywhere.
          </p>

          {(phase.kind === "drop" || phase.kind === "error") && (
            // biome-ignore lint/a11y/noStaticElementInteractions: drag-and-drop is a progressive enhancement over the "Choose a file" button below, which is what keyboard/AT users get.
            <div
              className={`${styles.dropzone} ${dragOver ? styles.dropzoneActive : ""}`}
              onDragOver={(event: DragEvent) => {
                event.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(event: DragEvent) => {
                event.preventDefault();
                setDragOver(false);
                const file = event.dataTransfer.files[0];
                if (file) void handleFile(file);
              }}
            >
              <p>Drop your Letterboxd export .zip here, or a bare diary.csv / watched.csv.</p>
              <button type="button" onClick={() => fileInputRef.current?.click()}>
                Choose a file
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".zip,.csv"
                className={styles.hiddenInput}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void handleFile(file);
                  event.target.value = "";
                }}
              />
              {phase.kind === "error" && <p className={styles.error}>{phase.message}</p>}
            </div>
          )}

          {phase.kind === "parsed" && (
            <div className={styles.results}>
              <p className={styles.summary}>
                {autoMatchedCount} matched automatically
                {reviewRows.length > 0 && `, ${reviewRows.length} need a quick confirm`}
                {unmatchedRows.length > 0 && `, ${unmatchedRows.length} unmatched`}.
              </p>

              <MatchReview
                rows={reviewRows}
                entryById={entryById}
                resolutions={resolutions}
                onResolve={(index, entryId) =>
                  setResolutions((prev) => ({ ...prev, [index]: entryId }))
                }
              />

              {unmatchedRows.length > 0 && (
                <details className={styles.unmatched}>
                  <summary>{unmatchedRows.length} unmatched, listed for transparency</summary>
                  <ul>
                    {unmatchedRows.map(({ index, record }) => (
                      <li key={index}>
                        {record.title}
                        {record.year ? ` (${record.year})` : ""}
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              {tv.entries.length > 0 && (
                <section className={styles.tvNote}>
                  <p>
                    {tv.entries.length} TV series can't be represented in a Letterboxd export.
                    Letterboxd is movies-only.
                  </p>
                  {tvAiredUnwatched.length > 0 && (
                    <button
                      type="button"
                      className={styles.tvButton}
                      onClick={() => markAllWatched(tvAiredUnwatched)}
                    >
                      Mark all {tvAiredUnwatched.length} aired series watched
                    </button>
                  )}
                </section>
              )}

              <div className={styles.diffPreview}>
                <h3 className={styles.heading}>
                  {importedCount === 0
                    ? "Nothing to import yet"
                    : `${diff.toAdd.length} to add, ${diff.toUpdate.length} to update${
                        mode === "replace" ? `, ${diff.toRemove.length} to remove` : ""
                      }`}
                </h3>
                <label className={styles.replaceOption}>
                  <input
                    type="checkbox"
                    checked={mode === "replace"}
                    onChange={(event) => setMode(event.target.checked ? "replace" : "additive")}
                  />
                  Replace my watched list instead of adding to it (drops anything this import
                  doesn't mention)
                </label>
                {(diff.toAdd.length > 0 ||
                  diff.toUpdate.length > 0 ||
                  diff.toRemove.length > 0) && (
                  <ul className={styles.diffList}>
                    {diff.toAdd.map((row) => (
                      <li key={`add-${row.entryId}`} className={styles.diffAdd}>
                        + {row.title}
                      </li>
                    ))}
                    {diff.toUpdate.map((row) => (
                      <li key={`update-${row.entryId}`} className={styles.diffUpdate}>
                        ~ {row.title}
                      </li>
                    ))}
                    {diff.toRemove.map((row) => (
                      <li key={`remove-${row.entryId}`} className={styles.diffRemove}>
                        − {row.title}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className={styles.actions}>
                <button type="button" onClick={reset}>
                  Start over
                </button>
                <button
                  type="button"
                  className={styles.commitButton}
                  disabled={importedCount === 0}
                  onClick={() => {
                    applyImport(importedRecords, mode);
                    setPhase({ kind: "done", diff });
                  }}
                >
                  Import {importedCount} {importedCount === 1 ? "film" : "films"}
                </button>
              </div>
            </div>
          )}

          {phase.kind === "done" && (
            <div className={styles.done}>
              <p>
                Added {phase.diff.toAdd.length}, updated {phase.diff.toUpdate.length}
                {mode === "replace" ? `, removed ${phase.diff.toRemove.length}` : ""}.
              </p>
              <button type="button" onClick={close}>
                Done
              </button>
            </div>
          )}
        </div>
      </dialog>
    </>
  );
}
